/**
 * Date Helper Utilities
 * Handles date/time formatting for API communication to avoid timezone issues
 */

/**
 * Formats a date for API submission
 * For date-only fields, returns YYYY-MM-DD format
 * For date-time fields, returns ISO string with local time (no timezone conversion)
 * 
 * @param {Date|string} date - The date to format
 * @param {boolean} includeTime - Whether to include time (default: false for date-only fields)
 * @returns {string|null} - Formatted date string or null
 */
export const formatDateForAPI = (date, includeTime = false) => {
  if (!date) return null;
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to formatDateForAPI:', date);
    return null;
  }
  
  if (includeTime) {
    // For date-time fields, format as local date/time without timezone conversion
    // Format: YYYY-MM-DDTHH:mm:ss (no Z suffix, so it's treated as local time)
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  } else {
    // For date-only fields, return YYYY-MM-DD format
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
};

/**
 * Formats a date for display
 * Converts UTC dates from API to local time for display
 * 
 * @param {string|Date} dateString - The date string from API (may be UTC)
 * @param {string} format - Display format ('date', 'datetime', 'time')
 * @returns {string} - Formatted date string
 */
export const formatDateForDisplay = (dateString, format = 'date') => {
  if (!dateString) return 'N/A';
  
  // Handle invalid dates
  if (typeof dateString === 'string' && dateString.startsWith("0001-01-01")) {
    return 'N/A';
  }
  
  const date = new Date(dateString);
  
  if (isNaN(date.getTime())) {
    return 'N/A';
  }
  
  if (format === 'date') {
    // Format as DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } else if (format === 'datetime') {
    // Format as DD/MM/YYYY HH:mm
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } else if (format === 'time') {
    // Format as HH:mm
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  
  return date.toLocaleDateString();
};

/**
 * Parses a date string from API and returns a Date object
 * Handles both UTC and local time strings
 * 
 * @param {string} dateString - Date string from API
 * @returns {Date|null} - Date object or null if invalid
 */
export const parseDateFromAPI = (dateString) => {
  if (!dateString) return null;
  
  // Handle invalid dates
  if (typeof dateString === 'string' && dateString.startsWith("0001-01-01")) {
    return null;
  }
  
  const date = new Date(dateString);
  
  if (isNaN(date.getTime())) {
    return null;
  }
  
  return date;
};

